import { RoadmapTask } from "@models";
import { useSearchParams } from "react-router-dom";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";

/**
 * Hook to update search params of the current url without removing existing params
 * @param params the urls query params to set or update
 * @returns a function to update search params
 */
export const useUpdateSearchParams = () => {
  const [_, setSearchParams] = useSearchParams();

  const updateSearchParams = (
    params: { [i: string]: string },
    removeParams?: string[]
  ) => {
    setSearchParams((searchParams) => {
      removeParams?.forEach((param) => {
        searchParams.delete(param);
      });
      Object.entries(params).forEach(([k, v]) => {
        searchParams.set(k, v);
      });
      return searchParams;
    });
  };

  return updateSearchParams;
};

export const fillRouteWithSlugs = (
  route: ROUTES,
  slugs: {
    [key in ROUTE_SLUGS]?: string;
  } = {}
) => {
  let fullRoute: string = route;
  Object.entries(slugs).forEach(([key, value]) => {
    fullRoute = fullRoute.replace(key, value);
  });
  return fullRoute;
};

export const clearTransientDocumentSearchParams = (
  searchParams: string | URLSearchParams
) => {
  const nextSearchParams = new URLSearchParams(searchParams);

  [
    "commentThreadId",
    "d",
    "deviceId",
    "diff",
    "step",
    "tiptapVersion",
    "v",
  ].forEach((key) => {
    nextSearchParams.delete(key);
  });

  return nextSearchParams;
};

export const buildDocumentRoute = (params: {
  documentId: string;
  deviceId: string;
  orgId: string;
  searchParams?: string | URLSearchParams;
  templateKey?: string | null;
  tiptapVersion?: number | null;
  versionId?: string;
}) => {
  const {
    documentId,
    deviceId,
    orgId,
    searchParams,
    tiptapVersion,
    versionId,
  } = params;
  const nextSearchParams = clearTransientDocumentSearchParams(
    searchParams ?? ""
  );

  if (versionId) {
    nextSearchParams.set("v", versionId);
  }

  if (typeof tiptapVersion === "number") {
    nextSearchParams.set("tiptapVersion", String(tiptapVersion));
  }

  const baseRoute = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
    [ROUTE_SLUGS.DOC_ID]: documentId,
    [ROUTE_SLUGS.DEVICE_ID]: deviceId,
    [ROUTE_SLUGS.ORG_ID]: orgId,
  });
  const searchSuffix = nextSearchParams.toString();

  return searchSuffix ? `${baseRoute}?${searchSuffix}` : baseRoute;
};

export const getDocumentNavigationUrl = (
  orgId: string,
  deviceId: string,
  docId: string,
  versionId?: string
) => {
  const url = fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
    [ROUTE_SLUGS.ORG_ID]: orgId,
    [ROUTE_SLUGS.DEVICE_ID]: deviceId,
    [ROUTE_SLUGS.DOC_ID]: docId,
  });
  return versionId ? `${url}?v=${versionId}` : url;
};

export const getRoadmapTaskLink = (
  roadmapTasks: RoadmapTask[],
  orgId: string,
  deviceId: string,
  templateKey: string
) => {
  const task = roadmapTasks.find((task) => task.taskKey === templateKey);

  if (!task) {
    throw new Error(
      `Cannot find task: '${templateKey}' in roadmap tasks to link to.`
    );
  }

  if (!task.parentCardKey) {
    throw new Error(
      `Task '${templateKey}' has no parentCardKey. Use a different navigation method for standalone tasks.`
    );
  }

  return (
    fillRouteWithSlugs(ROUTES.DEVICE_OVERVIEW, {
      [ROUTE_SLUGS.ORG_ID]: orgId,
      [ROUTE_SLUGS.DEVICE_ID]: deviceId,
    }) +
    "?card=" +
    task.parentCardKey +
    "&task=" +
    templateKey
  );
};
